import React from "react";
import { useTranslation } from "react-i18next";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// Widget imports
import LineChartWidget from "./widgets/LineChartWidget";
import BarChartWidget from "./widgets/BarChartWidget";
import GaugeWidget from "./widgets/GaugeWidget";
import StatusCardWidget from "./widgets/StatusCardWidget";
import TableWidget from "./widgets/TableWidget";
import KpiCardWidget from "./widgets/KpiCardWidget";
import EventFeedWidget from "./widgets/EventFeedWidget";
import { resolveGaugeRange } from "../../utils/gaugeRange";
import EventRangeBar from "./EventRangeBar";
import { formatViewerDateTime } from "../../utils/viewerTimeZone";

// Cihaz/tag seçimli widget'larda API'den gelen veriyi kullan
function getWidgetDataForRenderer(widget, widgetData) {
  const data = widgetData?.[widget.id];
  const config = widget.config || {};
  if (!config.edgeId || !config.deviceIds?.length || !config.tagNames?.length) return null;
  return data;
}

// Edge sistemi (CPU/RAM/Disk) widget'ı
function EdgeSystemWidget({ title, system }) {
  const { t } = useTranslation();
  const cpu = system?.cpu_percent ?? null;
  const mem = system?.mem_percent ?? null;
  const disk = system?.disk_percent ?? null;
  const ts = system?.timestamp;
  const formatPct = (v) => (v != null ? `${Number(v).toFixed(1)}%` : "—");
  const row = {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    padding: "12px",
    alignItems: "stretch",
  };
  const card = {
    flex: "1",
    minWidth: "70px",
    background: "var(--bg-hover)",
    borderRadius: "6px",
    padding: "10px 12px",
    textAlign: "center",
    border: "1px solid var(--border)",
  };
  const labelStyle = { fontSize: "11px", color: "var(--text-muted)", marginBottom: "4px" };
  const valueStyle = { fontSize: "18px", fontWeight: 600, color: "var(--text-primary)" };
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {title && (
        <div style={{ padding: "8px 12px 4px", fontSize: "14px", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
          {title}
        </div>
      )}
      <div style={row}>
        <div style={card}>
          <div style={labelStyle}>CPU</div>
          <div style={valueStyle}>{formatPct(cpu)}</div>
        </div>
        <div style={card}>
          <div style={labelStyle}>RAM</div>
          <div style={valueStyle}>{formatPct(mem)}</div>
        </div>
        <div style={card}>
          <div style={labelStyle}>Disk</div>
          <div style={valueStyle}>{formatPct(disk)}</div>
        </div>
      </div>
      {ts && (
        <div style={{ padding: "4px 12px 8px", fontSize: "11px", color: "var(--text-muted)" }}>
          {t("home.grid.lastUpdate", { time: formatViewerDateTime(ts) })}
        </div>
      )}
    </div>
  );
}

// Edge Logs widget — sadece error ve warning kayıtları
function EdgeLogsWidget({ title, logs }) {
  const { t } = useTranslation();
  const WARN_PCT = 70;
  const ERR_PCT = 90;

  // Her log kaydından event'leri çıkar (error + resource warning)
  const events = [];
  for (const r of (Array.isArray(logs) ? [...logs].reverse() : [])) {
    const ts = r.ts ?? r.timestamp ?? r.fetchedAt;
    const timeStr = formatViewerDateTime(ts);

    // Cihaz hataları
    let errors = {};
    try {
      if (r.device_errors && r.device_errors !== "{}") {
        errors = typeof r.device_errors === "string" ? JSON.parse(r.device_errors) : r.device_errors;
      }
    } catch (_) {}
    for (const [device, msg] of Object.entries(errors)) {
      events.push({ ts, timeStr, level: "error", label: device, msg });
    }

    // Resource uyarıları
    const resources = [
      { key: "cpu_percent", name: "CPU" },
      { key: "mem_percent", name: "RAM" },
      { key: "disk_percent", name: "Disk" },
    ];
    for (const { key, name } of resources) {
      const val = r[key];
      if (val == null) continue;
      if (val >= ERR_PCT) {
        events.push({ ts, timeStr, level: "error", label: name, msg: t("home.grid.logCritical", { pct: Number(val).toFixed(1) }) });
      } else if (val >= WARN_PCT) {
        events.push({ ts, timeStr, level: "warning", label: name, msg: t("home.grid.logHigh", { pct: Number(val).toFixed(1) }) });
      }
    }
  }

  const levelStyle = (level) => level === "error"
    ? { color: "var(--danger)", bg: "rgba(248,81,73,0.12)", border: "rgba(248,81,73,0.3)" }
    : { color: "#f0883e", bg: "rgba(240,136,62,0.12)", border: "rgba(240,136,62,0.3)" };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {title && (
        <div style={{ padding: "8px 12px 4px", fontSize: "14px", color: "var(--text-muted)", borderBottom: "1px solid var(--border)", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{title}</span>
          {events.length > 0 && (
            <span style={{ fontSize: "11px", backgroundColor: "rgba(248,81,73,0.15)", color: "var(--danger)", border: "1px solid rgba(248,81,73,0.3)", borderRadius: "10px", padding: "1px 8px" }}>
              {events.length}
            </span>
          )}
        </div>
      )}
      {events.length === 0 ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#3fb950", gap: "4px" }}>
          <span style={{ fontSize: "20px" }}>✓</span>
          <span style={{ fontSize: "12px" }}>{t("home.grid.logNone")}</span>
        </div>
      ) : (
        <div style={{ overflowY: "auto", flex: 1, padding: "6px 8px", display: "flex", flexDirection: "column", gap: "4px" }}>
          {events.map((ev, i) => {
            const s = levelStyle(ev.level);
            return (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", backgroundColor: s.bg, border: `1px solid ${s.border}`, borderRadius: "6px", padding: "6px 10px", fontSize: "12px" }}>
                <span style={{ flexShrink: 0, fontWeight: 700, color: s.color, minWidth: "40px" }}>
                  {ev.level === "error" ? "ERR" : "WARN"}
                </span>
                <span style={{ flexShrink: 0, fontWeight: 600, color: s.color, minWidth: "36px" }}>
                  {ev.label}
                </span>
                <span style={{ flex: 1, color: "var(--text-primary)" }}>{ev.msg}</span>
                <span style={{ flexShrink: 0, fontSize: "11px", color: "var(--text-muted)" }}>{ev.timeStr}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Widget renderer: hem edge cihaz/tag verisi (widgetData) hem eski metric (sensorData) destekler
// An alarm / anomaly table with its own time range above it (see utils/eventRange.js)
function withRangeBar(widget, widgetData, kind, onChange, empty, table) {
  const d = widgetData?.[widget.id] || {};
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <EventRangeBar widgetId={widget.id} choice={d.choice || "7d"} range={d.range || "7d"} empty={empty} kind={kind} lastAt={d.lastAt} onChange={onChange} />
      <div style={{ flex: 1, minHeight: 0 }}>{table}</div>
    </div>
  );
}

export const WidgetRenderer = ({ widget, sensorData, widgetData, onEventRangeChange }) => {
  const { t } = useTranslation();
  const col = (key, labelKey) => ({ key, label: t(`home.grid.col.${labelKey || key}`) });
  const type = widget.type || "statuscard";
  const config = widget.config || {};
  const dashData = getWidgetDataForRenderer(widget, widgetData);

  if (type === "edge_system") {
    const sys = widgetData?.[widget.id]?.system;
    return <EdgeSystemWidget title={config.title} system={sys} />;
  }

  if (type === "edge_logs") {
    const logs = widgetData?.[widget.id]?.logs ?? [];
    return <EdgeLogsWidget title={config.title} logs={logs} />;
  }

  if (type === "alarm_table") {
    const rows = widgetData?.[widget.id]?.alarms || [];
    const columns = [col("time"), col("field"), col("severity"), col("value"), col("threshold"), col("message")];
    return withRangeBar(widget, widgetData, "alarm", onEventRangeChange, rows.length === 0,
      <TableWidget title={config.title || t("home.titles.alarm_table")} columns={columns} data={rows} />);
  }

  if (type === "anomaly_table") {
    const rows = widgetData?.[widget.id]?.anomalies || [];
    const columns = [col("time"), col("field"), col("level"), col("value"), col("score")];
    return withRangeBar(widget, widgetData, "anomaly", onEventRangeChange, rows.length === 0,
      <TableWidget title={config.title || t("home.titles.anomaly_table")} columns={columns} data={rows} />);
  }

  if (type === "event_feed") {
    const events = widgetData?.[widget.id]?.events || [];
    return withRangeBar(widget, widgetData, "event", onEventRangeChange, events.length === 0,
      <EventFeedWidget title={config.title || t("home.titles.event_feed")} events={events} />);
  }

  if (dashData) {
    const latest = dashData.latest || [];
    const series = dashData.series || [];
    const title = config.title || (config.tagNames && config.tagNames.join(", ")) || t("home.titles.data");
    const unit = config.unit || (latest[0] && latest[0].unit) || "";

    if (type === "gauge") {
      const value = latest[0]?.value ?? 0;
      const stat = (dashData.stats || []).find(
        (s) => s.tag_name === latest[0]?.tag_name && s.device_id === latest[0]?.device_id
      ) || dashData.stats?.[0];
      const range = resolveGaugeRange(config, stat, value);
      return (
        <GaugeWidget
          title={title}
          value={value}
          unit={unit}
          min={range.min}
          max={range.max}
          auto={range.auto}
          outOfRange={range.outOfRange}
          thresholds={config.thresholds || { warning: 70, danger: 90 }}
        />
      );
    }
    if (type === "kpi") {
      const values = [...series]
        .filter((p) => p.tag_name === latest[0]?.tag_name && p.device_id === latest[0]?.device_id)
        .sort((a, b) => (a.time > b.time ? 1 : -1))
        .map((p) => p.value);
      return <KpiCardWidget title={title} value={latest[0]?.value ?? "-"} unit={unit} values={values} />;
    }
    if (type === "linechart") {
      const rawTimes = [...new Set(series.map((p) => p.time))].sort();
      const labels = rawTimes.map((time) =>
        formatViewerDateTime(time, undefined, { hour: "2-digit", minute: "2-digit" })
      );
      const bySeries = {};
      series.forEach((p) => {
        const tagDisplay = p.tag_label || p.tag_name;
        const key = `${p.device_name || p.device_id} · ${tagDisplay}`;
        if (!bySeries[key]) bySeries[key] = [];
        bySeries[key].push(p);
      });
      const datasets = Object.entries(bySeries).map(([label, points]) => ({
        label,
        data: rawTimes.map((time) => {
          const pt = points.find((d) => d.time === time);
          return pt ? pt.value : null;
        }),
      }));
      return (
        <LineChartWidget
          title={title}
          labels={labels}
          unit={unit}
          color={config.color || "#2c5282"}
          data={datasets[0]?.data || []}
          datasets={datasets}
        />
      );
    }
    if (type === "table") {
      const columns = [col("time"), col("device_name", "device"), col("tag_display", "tag"), col("value"), col("unit")];
      const rowsFromSeries = [...(series || [])]
        .sort((a, b) => (a.time > b.time ? -1 : 1))
        .slice(0, 30)
        .map((r) => ({
          time: formatViewerDateTime(r.time, undefined, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }),
          device_name: r.device_name || r.device_id,
          tag_display: r.tag_label || r.tag_name,
          value: r.value != null ? Number(r.value).toFixed(2) : "-",
          unit: r.unit || "-",
        }));
      const rows =
        rowsFromSeries.length > 0
          ? rowsFromSeries
          : latest.map((r) => ({
              time: r.time
                ? formatViewerDateTime(r.time, undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
              device_name: r.device_name || r.device_id,
              tag_display: r.tag_label || r.tag_name,
              value: r.value != null ? Number(r.value).toFixed(2) : "-",
              unit: r.unit || "-",
            }));
      return <TableWidget title={title} columns={columns} data={rows} />;
    }
    if (type === "statuscard") {
      const value = latest[0]?.value ?? "-";
      return (
        <StatusCardWidget
          title={title}
          value={value}
          unit={unit}
          color={config.color || "green"}
        />
      );
    }
  }

  switch (type) {
    case "linechart":
      return (
        <LineChartWidget
          title={config.title}
          data={config.data}
          labels={config.labels}
          unit={config.unit}
          color={config.color}
        />
      );
    case "barchart":
      return (
        <BarChartWidget
          title={config.title}
          data={config.data}
          labels={config.labels}
          unit={config.unit}
          color={config.color}
          datasets={config.datasets}
        />
      );
    case "gauge":
      return (
        <GaugeWidget
          title={config.title}
          value={sensorData?.[config.metric] ?? config.value ?? 0}
          unit={config.unit}
          min={config.min}
          max={config.max}
          thresholds={config.thresholds}
        />
      );
    case "statuscard":
      return (
        <StatusCardWidget
          title={config.title}
          value={sensorData?.[config.metric] ?? config.value}
          unit={config.unit}
          color={config.color}
          icon={config.icon}
          subtitle={config.subtitle}
        />
      );
    case "table":
      return (
        <TableWidget
          title={config.title}
          columns={config.columns}
          data={config.data}
        />
      );
    default:
      return <div style={{ padding: 16, color: "var(--text-muted)" }}>{t("home.grid.unknownWidget", { type })}</div>;
  }
};

export default function DashboardGrid({
  widgets,
  layout,
  onLayoutChange,
  onRemoveWidget,
  sensorData,
  widgetData,
  onEventRangeChange,
  editable = false,
  cols = 12,
  rowHeight = 80,
  width = 1200,
}) {
  const { t } = useTranslation();
  const gridLayout = layout || widgets.map((w, i) => ({
    i: w.id,
    x: w.position?.x ?? (i % 4) * 3,
    y: w.position?.y ?? Math.floor(i / 4) * 3,
    w: w.position?.w ?? 3,
    h: w.position?.h ?? 3,
    minW: 1,
    minH: 1,
  }));

  const handleLayoutChange = (newLayout) => {
    if (onLayoutChange) {
      const updatedLayout = newLayout.map((item) => ({
        id: item.i,
        position: { x: item.x, y: item.y, w: item.w, h: item.h },
      }));
      onLayoutChange(updatedLayout);
    }
  };

  return (
    <div style={styles.container}>
      <GridLayout
        className="layout"
        layout={gridLayout}
        cols={cols}
        rowHeight={rowHeight}
        width={width}
        onLayoutChange={handleLayoutChange}
        isDraggable={editable}
        isResizable={editable}
        draggableHandle=".widget-drag-handle"
        margin={[8, 8]}
      >
        {widgets.map((widget) => (
          <div key={widget.id} style={styles.widgetWrapper}>
            {editable && (
              <div style={styles.widgetActions}>
                <div className="widget-drag-handle" style={styles.dragHandle}>
                  ⋮⋮
                </div>
                {onRemoveWidget && (
                  <button
                    type="button"
                    style={styles.deleteBtn}
                    onClick={() => onRemoveWidget(widget.id)}
                    title={t("home.grid.deleteWidget")}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            <WidgetRenderer
              widget={widget}
              sensorData={sensorData}
              widgetData={widgetData}
              onEventRangeChange={onEventRangeChange}
            />
          </div>
        ))}
      </GridLayout>
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
  },
  widgetWrapper: {
    backgroundColor: "var(--vx-surface)",
    borderRadius: "var(--vx-radius)",
    overflow: "hidden",
    border: "1px solid var(--vx-line)",
  },
  widgetActions: {
    position: "absolute",
    top: "4px",
    right: "8px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    zIndex: 10,
  },
  dragHandle: {
    cursor: "grab",
    color: "var(--text-muted)",
    fontSize: "14px",
    padding: "4px",
  },
  deleteBtn: {
    background: "rgba(248, 81, 73, 0.2)",
    color: "var(--danger)",
    border: "1px solid var(--danger)",
    borderRadius: "4px",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
    margin: 0,
  },
};
